/**
 * 950 / 12.9K / 4.2M: token counts at a glance; exact figures go in titles.
 * en-US for the capital K (en-GB writes "12.9k").
 */
const COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
export const fmtTokens = (n: number): string => COMPACT.format(n);

/** Share of a whole as "63%", or "–" when there is no whole. */
export function pct(part: number, whole: number): string {
  if (!whole) return "–";
  const p = (part / whole) * 100;
  return p > 0 && p < 1 ? "<1%" : `${Math.round(p)}%`;
}

/**
 * Each agent's colour is a fixed slot (--chart-s1..s8 in index.css), never
 * its rank, so it keeps its colour on every range and on both servers. Eight
 * is as many hues as stay tellable apart; any other agent shares Other.
 * Names follow agentForTask in backend/src/deployments/llm-log.ts.
 */
export const AGENT_SLOTS = [
  "Main Cipher",
  "Assistant Cipher",
  "Quantile",
  "Deal Origin",
  "Fulcrum",
  "Company Research",
  "Reporting Agent",
  "ICM Agent",
] as const;

export const OTHER = "Other";

/** The CSS colour token for an agent: its slot, or Other's. */
export function agentToken(agent: string): string {
  const i = (AGENT_SLOTS as readonly string[]).indexOf(agent);
  return i >= 0 ? `--chart-s${i + 1}` : "--chart-other";
}

export interface AgentSeries {
  name: string;
  token: string;
  /** Tokens per day, in the days' order. */
  data: number[];
  /** For Other: the agents folded into it. */
  members: string[];
}

/**
 * The stacked chart's series: each slotted agent with tokens in the range, in
 * slot order (bottom up), then one Other for the rest.
 */
export function agentSeries(byDay: { byAgent: Record<string, number> }[]): AgentSeries[] {
  const present = new Set(byDay.flatMap((d) => Object.keys(d.byAgent).filter((a) => d.byAgent[a] > 0)));
  const series: AgentSeries[] = AGENT_SLOTS.filter((a) => present.has(a)).map((a) => ({
    name: a,
    token: agentToken(a),
    data: byDay.map((d) => d.byAgent[a] ?? 0),
    members: [a],
  }));
  const rest = [...present].filter((a) => agentToken(a) === "--chart-other").sort();
  if (rest.length) {
    series.push({
      name: OTHER,
      token: "--chart-other",
      data: byDay.map((d) => rest.reduce((n, a) => n + (d.byAgent[a] ?? 0), 0)),
      members: rest,
    });
  }
  return series;
}

/** "Wed 28 Oct" from a UTC "2026-10-28". */
export const dayLabel = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
